import { useMemo, useState } from "react";
import {
  describeRoot,
  measureSource,
  proposeCollectionPaths,
  proposeDelta,
  type SourceMeasurement
} from "../../engine/profile-inference";
import { PROFILES, PROFILE_BASE } from "../../profiles";
import type { SavedDraftProfile } from "../../db";

/**
 * Measure the uploaded pair and offer the result as a draft profile.
 *
 * Shows the evidence before the verdict, and says plainly what it withheld.
 * A person approving a profile they cannot see the basis for is the failure
 * this panel exists to avoid — so the measurement table and the withheld list
 * are not behind a disclosure, they are the panel.
 */

export type GenerateProfilePanelProps = {
  baselineDataset: unknown;
  latestDataset: unknown;
  baselineFileName: string;
  latestFileName: string;
  onSave: (row: SavedDraftProfile) => Promise<void>;
  onCancel: () => void;
};

const identitySignature = (values: Record<string, string[]> | undefined): string =>
  JSON.stringify(Object.entries(values ?? {}).sort(([a], [b]) => a.localeCompare(b)));

export function GenerateProfilePanel({
  baselineDataset,
  latestDataset,
  baselineFileName,
  latestFileName,
  onSave,
  onCancel
}: GenerateProfilePanelProps) {
  const candidates = useMemo(() => proposeCollectionPaths(baselineDataset), [baselineDataset]);
  // Preselect only when there is exactly one: with several, the records array
  // is a guess, and guessing a records path is inventing a source schema.
  const [collectionPath, setCollectionPath] = useState(() =>
    candidates.length === 1 ? candidates[0]!.path : ""
  );
  const [draftId, setDraftId] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const measurement: SourceMeasurement | null = useMemo(
    () =>
      collectionPath
        ? measureSource({
            baseline: baselineDataset,
            latest: latestDataset,
            collectionPath,
            titleField: PROFILE_BASE?.alerts?.duplicateTitle?.field
          })
        : null,
    [baselineDataset, latestDataset, collectionPath]
  );

  const proposal = useMemo(() => {
    if (!measurement || !PROFILE_BASE) return null;
    return proposeDelta(measurement, PROFILE_BASE, {
      id: draftId || "measured-source",
      ...(sourceUrl ? { sourceUrl } : {}),
      ...(displayName ? { displayName } : {})
    });
  }, [measurement, draftId, sourceUrl, displayName]);

  /**
   * Identity is the authoritative discriminator, so a match means this is not a
   * new source at all — it is one already registered, and the right move is to
   * pick that profile rather than measure a second one claiming the same bot.
   */
  const collision = useMemo(() => {
    const proposed = proposal?.delta.detection?.identityValues;
    if (!proposed || Object.keys(proposed).length === 0) return null;
    const signature = identitySignature(proposed);
    return (
      Object.values(PROFILES).find(
        (profile) => identitySignature(profile.detection?.identityValues) === signature
      ) ?? null
    );
  }, [proposal]);

  const urlCollision = useMemo(() => {
    const proposed = proposal?.delta.sourceUrl;
    if (!proposed) return null;
    return Object.values(PROFILES).find((profile) => profile.sourceUrl === proposed) ?? null;
  }, [proposal]);

  const idValid = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(draftId);
  const blocked = proposal?.blocked ?? [];
  const canSave =
    proposal !== null && blocked.length === 0 && idValid && collision === null && urlCollision === null && !saving;

  const save = async () => {
    if (!proposal || !measurement) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        draftId,
        delta: { ...proposal.delta, id: draftId },
        measurement,
        createdAt: new Date().toISOString(),
        baselineFileName,
        latestFileName
      });
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
      setSaving(false);
    }
  };

  const downloadDelta = () => {
    if (!proposal) return;
    const blob = new Blob([`${JSON.stringify({ ...proposal.delta, id: draftId || "measured-source" }, null, 2)}\n`], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${draftId || "measured-source"}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      data-testid="generate-profile-panel"
      className="mt-3 rounded-sm border border-slate-300 bg-white p-4"
      aria-label="Generate a source profile from these files"
    >
      <h2 className="text-lg font-semibold">Measure these files and propose a profile</h2>
      <p className="mt-1 text-sm text-slate-600">
        Everything below is measured from the two uploaded files. Nothing is assumed, and what cannot be measured is
        left out and named rather than filled in with a plausible default.
      </p>

      <label className="mt-4 block text-sm">
        <span className="mb-1 block font-medium">Where the records are</span>
        {candidates.length === 0 ? (
          <span data-testid="no-collection-candidates" className="block rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            No key in the baseline holds an array of objects, so there is nothing to measure. Root keys present:{" "}
            {describeRoot(baselineDataset)}.
          </span>
        ) : (
          <select
            data-testid="collection-path-select"
            className="w-full rounded-sm border border-slate-300 p-2"
            value={collectionPath}
            onChange={(event) => setCollectionPath(event.target.value)}
          >
            <option value="">Choose the records array…</option>
            {candidates.map((candidate) => (
              <option key={candidate.path} value={candidate.path}>
                {candidate.path} — {candidate.recordCount} records, {candidate.fieldCount} fields
              </option>
            ))}
          </select>
        )}
        {candidates.length > 1 ? (
          <span className="mt-1 block text-xs text-slate-600">
            More than one key holds an array of objects. Which one holds the records is a fact about this source, not
            something to infer, so pick it.
          </span>
        ) : null}
      </label>

      {measurement && proposal ? (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Profile id</span>
              <input
                data-testid="draft-id-input"
                className="w-full rounded-sm border border-slate-300 p-2"
                value={draftId}
                onChange={(event) => setDraftId(event.target.value)}
                placeholder="county-portal"
              />
              {draftId && !idValid ? (
                <span className="mt-1 block text-xs text-red-700">Must be kebab-case: lowercase, digits, hyphens.</span>
              ) : null}
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Source URL</span>
              <input
                data-testid="draft-source-url-input"
                className="w-full rounded-sm border border-slate-300 p-2"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder={proposal.delta.sourceUrl}
              />
              <span className="mt-1 block text-xs text-slate-600">
                Measured: {proposal.delta.sourceUrl || "none"}
              </span>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Display name</span>
              <input
                data-testid="draft-display-name-input"
                className="w-full rounded-sm border border-slate-300 p-2"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </label>
          </div>

          <h3 className="mt-4 text-sm font-semibold">What the files say</h3>
          <div className="mt-1 overflow-x-auto">
            <table data-testid="measurement-table" className="w-full text-left text-xs">
              <caption className="sr-only">Measurements taken from the uploaded pair</caption>
              <thead>
                <tr className="border-b border-slate-300">
                  <th scope="col" className="py-1 pr-3 font-medium">Measurement</th>
                  <th scope="col" className="py-1 font-medium">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row" className="py-1 pr-3 font-normal">Records</th>
                  <td className="py-1">
                    {measurement.baselineRecordCount} baseline, {measurement.latestRecordCount} latest
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="py-1 pr-3 font-normal">Fields</th>
                  <td className="py-1">
                    {measurement.fields.length} across the pair, {measurement.neverPopulatedFields.length} empty in
                    every record
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="py-1 pr-3 font-normal">Bot identity</th>
                  <td className="py-1">
                    {Object.entries(proposal.delta.detection?.identityValues ?? {})
                      .map(([field, values]) => `${field} ${values[0]}`)
                      .join(", ") || "none constant"}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="py-1 pr-3 font-normal">Proposed key</th>
                  <td className="py-1">{(proposal.delta.primaryKey ?? []).join(" + ") || "none qualified"}</td>
                </tr>
                <tr>
                  <th scope="row" className="py-1 pr-3 font-normal">Observed match rate</th>
                  <td className="py-1">
                    {measurement.keyCandidates.find(
                      (candidate) => candidate.fields.join(",") === (proposal.delta.primaryKey ?? []).join(",")
                    )?.matchRate?.toFixed(3) ?? "—"}{" "}
                    over one pair, which is an observation and not a floor
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="mt-4 text-sm font-semibold">What this cannot tell you</h3>
          <ul data-testid="withheld-list" className="mt-1 list-disc pl-5 text-xs text-slate-700">
            <li>
              <strong>No field is approved for backfill.</strong> Approval is a decision a person makes per source, so
              a measured profile always starts with none and can never grant one.
            </li>
            <li>
              <strong>No match-rate floor.</strong> One pair is a single churn observation. The base value applies
              until someone sets a floor for this source.
            </li>
            <li>
              <strong>No validation rules.</strong> Observed formats have to be checked against the parsers before they
              can fail a record.
            </li>
            <li>
              <strong>No recovered export.</strong> A draft can report on a run, but it cannot release recovered data
              until the delta is committed to the repo.
            </li>
          </ul>

          {proposal.warnings.map((warning) => (
            <p key={warning} data-testid="draft-warning" className="mt-2 rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
              {warning}
            </p>
          ))}

          {blocked.map((reason) => (
            <p key={reason} data-testid="draft-blocked" className="mt-2 rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700">
              {reason}
            </p>
          ))}

          {collision ? (
            <p data-testid="draft-identity-collision" className="mt-2 rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700">
              These files carry the same bot identity as{" "}
              <strong>{collision.displayName ?? collision.id}</strong>. This is not a new source — pick that profile
              instead.
            </p>
          ) : null}

          {!collision && urlCollision ? (
            <p data-testid="draft-url-collision" className="mt-2 rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700">
              The measured source URL is already claimed by{" "}
              <strong>{urlCollision.displayName ?? urlCollision.id}</strong>. Give this source a URL that tells it
              apart, or pick that profile.
            </p>
          ) : null}

          {saveError ? (
            <p data-testid="draft-save-error" className="mt-2 rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700">
              The draft was not saved: {saveError}
            </p>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              data-testid="save-draft-profile"
              className="rounded-sm bg-sky-700 px-3 py-2 text-sm text-white disabled:bg-slate-400"
              disabled={!canSave}
              onClick={() => void save()}
            >
              Use this draft
            </button>
            <button
              data-testid="download-draft-delta"
              className="rounded-sm border border-slate-400 px-3 py-2 text-sm disabled:text-slate-400"
              disabled={blocked.length > 0}
              onClick={downloadDelta}
            >
              Download delta JSON
            </button>
            <button className="text-sm text-slate-600 underline" onClick={onCancel}>
              Cancel
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-600">
            The downloaded file is what <code>src/profiles/sources/{draftId || "measured-source"}.json</code> should
            contain, evidence notes included. Committing it turns this draft into policy.
          </p>
        </>
      ) : null}
    </section>
  );
}
